export interface INodeLeaf {
    id: number;
    children?: INodeLeaf[] | null;
}

export interface ITemplate {
    id: number;
    parentId?: number;
    name: string;
    description?: string;
    isFolder: boolean;
    children?: ITemplate[] | null;
    noStages?: boolean | null;
    timeType?: TemplateTimeType | null;
    asdaWeekRange?: number;
    isFixedTemplate: boolean;
    isPredefinedTemplate?: boolean;
}

export interface ITemplateStage {
    desc: string;
    isMandatory: boolean;
    stageType: TemplateStageType;
}

export enum TemplateTimeType {
    PAST = 'PAST',
    FUTURE = 'FUTURE',
}

export enum TemplateStageType {
    COLUMNS = 'COLUMNS',
    ITEMS = 'ITEMS',
    LOCATIONS = 'LOCATIONS',
    TIMES = 'TIMES',
    EVENTS = 'EVENTS',
    OPTIONS = 'OPTIONS',
    ADJUSTMENTS = 'ADJUSTMENTS',
    REMITTANCES = 'REMITTANCES',
    FILTERS = 'FILTERS',
    CUSTOMERS = 'CUSTOMERS',
    DEPARTMENTS = 'DEPARTMENTS',
    REPORT_FILTERS = 'REPORT_FILTERS',
    POS = 'POS',
    SALES_TYPES = 'SALES_TYPES',
}

export interface ITemplateStageSection {
    id: number;
    name: string;
    type: TemplateStageSectionType;
    nodes: ITemplateStageSectionNode[];
    allowEditing: any;
    stageType: TemplateStageType;
}

export enum TemplateStageSectionType {
    COLUMN_PICKER = 'COLUMN_PICKER',
    HIERARCHY_FILTER = 'HIERARCHY_FILTER',
    CUSTOM_FILTER = 'CUSTOM_FILTER',
    PREDEFINED_FILTER = 'PREDEFINED_FILTER',
    CUSTOM_GROUPS = 'CUSTOM_GROUPS',
}

export interface ITemplateStageSectionNode {
    id: number;
    parentId: number;
    name: string;
    desc: string;
    formatHint: string;
    isFolder: boolean;
    isSelectable: boolean;
    isFixed: boolean;
    validationRegex: string;
    dataType: TemplateStageSectionNodeDataType;
    children?: ITemplateStageSectionNode[] | null;
    availableFilters: TemplateStageSectionNodeFilter[];
    sectionId: number;
    childrenCount: number;
    path?: string;
    datamartTable?: string;
    datamartColumn?: string;
    datamartValue?: string;
    measureId?: number;
    measureName?: string;
    measureTables?: string;
    measureValue?: string;
    measureColumns?: string;
    formatType?: NodeFormatType;
    visible?: boolean;
    isDatamartError?: boolean;
    hasDebugInfo?: boolean;
    type?: SelectionType;
}

export enum TemplateStageSectionNodeFilter {
    ONE_OF = 'ONE_OF',
    NOT_ONE_OF = 'NOT_ONE_OF',
    BETWEEN = 'BETWEEN',
    NOT_BETWEEN = 'NOT_BETWEEN',
    LIKE = 'LIKE',
    NOT_LIKE = 'NOT_LIKE',
    EQUAL = 'EQUAL',
    NOT_EQUAL = 'NOT_EQUAL',
    GREATER_THAN = 'GREATER_THAN',
    GREATER_OR_EQUAL_THAN = 'GREATER_OR_EQUAL_THAN',
    LESS_THAN = 'LESS_THAN',
    LESS_OR_EQUAL_THAN = 'LESS_OR_EQUAL_THAN',
}

export enum TemplateStageSectionNodeDataType {
    STRING = 'STRING',
    INTEGER = 'INTEGER',
    DECIMAL = 'DECIMAL',
    DATE = 'DATE',
    TIME = 'TIME',
    DATETIME = 'DATETIME',
}

export enum UploadMetadataType {
    COLUMNS = 'COLUMNS',
    ITEMFILTERS = 'ITEMFILTERS',
    LOCATIONFILTERS = 'LOCATIONFILTERS',
}

export interface IReportResponse {
    id: number;
    name: string;
    createdBy: string;
    createdAt: string;
    updatedBy: string;
    updatedAt: string;
    templateId: number;
    folderId: number;
    templateName: string;
    outputStyle: ReportOutputStyle;
    isShared: boolean;
    isScheduled: boolean;
    lastScheduledRunAt: string;
    scheduleStart: string;
    scheduleEnd: string;
    frequencyType: ReportFrequencyType;
    frequencyOnceDate: string;
    frequencyDayOfTheWeek: ReportFrequencyDayOfTheWeek;
    frequencyDayOfTheMonth: number;
    frequencyMonth: number;
    config: IReportConfigStage[];
    timeConfig: IReportTimeConfig[];
    timeAdvancedConfig: IReportTimeAdvancedConfig[];
    templateTimeType: TemplateTimeType;
    isHidden: boolean;
    isPredefined?: boolean;
    isNotificationEnabled: boolean;
}

export interface IReportRunResponse {
    id: number;
    reportId: number;
    reportName: string;
    createdBy: string;
    createdAt: string;
    runType: ReportRunType;
    status: ReportRunStatus;
    hadError: boolean;
    queryStartedAt: string;
    queryCompletedAt: string;
    outputFile: IRunNowOutputFile[];
    retryCount?: number;
    rowCount?: number;
    lastRetryFailedAt?: string;
    formatStartedAt?: string;
    formatCompletedAt?: string;
    formattedRows?: number;
    isFixedTemplate: boolean;
    // Optional Value, Added for showcasing the exact Report status on status column in ReportStatus page
    displayStatus: ReportRunStatus;
}

export interface ILastReportRunResponse {
    id: number;
    name: string;
    createdAt: string;
    status: ReportRunStatus;
    formatCompletedAt?: string;
}

export enum ReportRunType {
    SCHEDULED = 'SCHEDULED',
    RUN_NOW = 'RUN_NOW',
}

export enum ReportRunStatus {
    QUEUED = 'QUEUED',
    IN_PROGRESS_SQL = 'IN_PROGRESS_SQL',
    WAITING_FORMAT = 'WAITING_FORMAT',
    IN_PROGRESS_FORMAT = 'IN_PROGRESS_FORMAT',
    COMPLETED = 'COMPLETED',
    QUEUED_FOR_FORMATTING = 'QUEUED_FOR_FORMATTING',
    READY_FOR_PROCESSING = 'READY_FOR_PROCESSING',
    PRE_PROCESSING = 'PRE_PROCESSING',
    AWAITING_PRIORITISATION = 'AWAITING_PRIORITISATION',
}

export interface IReportTimeAdvancedConfig {
    id?: string;
    name: string;
    start: IReportTimeAdvancedConfigPoint;
    end?: IReportTimeAdvancedConfigPoint;
}

export interface IReportTimeStandardConfig {
    id?: string;
    name: string;
    timePeriod: IReportTimePeriod;
}
export interface IReportTimeConfig {
    // timePeriod: IReportTimeConfigPoint
    isAdvanced?: boolean;
    id?: string;
    name: string;
    start: IReportTimeAdvancedConfigPoint;
    end?: IReportTimeAdvancedConfigPoint;
}

export interface AsdaMonth {
    name: string;
    startDate: Date;
    endDate: Date;
}

export interface IReportTimeAdvancedConfigPoint {
    type?: TimeConfigType;
    yearType?: TimeConfigYearType;
    yearsBefore?: number;
    date?: string;
    month?: number;
    before?: number;
    walmartWeek?: string;
    asdaWeek?: number;
    effectiveDate?: string;

    year?: number;
    asdaMonth?: string;
    asdaYear?: number;
    AsdaFiscalYearToDate?: number;
    currentWeek?: string;
    lastWeek?: string;
    asdaWeeksBefore?: string;
    lastYearCurrentWeek?: string;
    lastYearLastWeek?: string;
    lastYearWeeksBefore?: string;
    WeekXBefore?: number;
}
export interface IReportTimeConfigPoint {
    type?: TimePeriodConfigType;
    asdaYear?: number;
    asdaMonth?: string;
    asdaWeek?: number;
    currentWeek?: number;
    lastWeek?: number;
    lastYearCurrentWeek?: number;
    lastYearLastWeek?: number;
    weekBeforeLastWeek?: number;
    lastYearWeekBeforeLastWeek?: number;
    fixedDate?: string;
    yearType?: string;
    from?: string;
    to?: string;
}

export interface IReportTimePeriod {
    type?: TimePeriodConfigType;
    asdaYear?: number;
    asdaFiscalYear?: number;
    asdaMonth?: string;
    asdaWeek?: string;
    fixedDate?: string;
    from?: string;
    to?: string;
    currentWeek?: number;
    lastYearCurrentWeek?: number;
    lastYearLastXWeek?: number;
    lastXWeeks?: number;
    WeekXBefore?: number;
    lastYearWeekXBefore?: number;
    yearType?: TimeConfigYearType;
    yearsBefore?: number;
    before?: number;
    effectiveDate?: string;
}

export enum ReportTimeConfigPointField {
    type = 'type',
    yearType = 'yearType',
    yearsBefore = 'yearsBefore',
    date = 'date',
    month = 'month',
    before = 'before',
    walmartWeek = 'walmartWeek',
    asdaWeek = 'asdaWeek',
    effectiveDate = 'effectiveDate',
}

export enum ReportTimeConfigPointPosition {
    START = 'START',
    END = 'END',
}

export enum TimeConfigType {
    TODAY = 'TODAY',
    DATE = 'DATE',
    DAYS_BEFORE = 'DAYS_BEFORE',
    CALENDAR_WEEKS_BEFORE = 'CALENDAR_WEEKS_BEFORE',
    ASDA_WEEKS_BEFORE = 'ASDA_WEEKS_BEFORE',
    MONTH = 'MONTH',
    MONTHS_BEFORE = 'MONTHS_BEFORE',
    WALMART_WEEK = 'WALMART_WEEK',
    ASDA_WEEK = 'ASDA_WEEK',
    WHOLE_YEAR = 'WHOLE_YEAR',
    LAST_WEEK = 'LAST_WEEK',
    FIXED_DATE = 'FIXED_DATE',
    ASDA_MONTH = 'ASDA_MONTH',
    ASDA_YEAR = 'ASDA_YEAR',
    ASDA_FISCAL_YEAR_TO_DATE = 'ASDA_FISCAL_YEAR_TO_DATE',
    CURRENT_WEEK = 'CURRENT_WEEK',
    LAST_YEAR_WEEKS_BEFORE = 'LAST_YEAR_WEEKS_BEFORE',
    LAST_YEAR_CURRENT_WEEK = 'LAST_YEAR_CURRENT_WEEK',
    LAST_YEAR_LAST_WEEK = 'LAST_YEAR_LAST_WEEK',
}

export enum TimeConfigStandardType {
    DATE = 'DATE',
    ASDA_WEEKS_BEFORE = 'ASDA_WEEKS_BEFORE',
    ASDA_WEEK = 'ASDA_WEEK',
    LAST_WEEK = 'LAST_WEEK',
    ASDA_MONTH = 'ASDA_MONTH',
    ASDA_YEAR = 'ASDA_YEAR',
    ASDA_FISCAL_YEAR_TO_DATE = 'ASDA_FISCAL_YEAR_TO_DATE',
    CURRENT_WEEK = 'CURRENT_WEEK',
    LAST_YEAR_WEEKS_BEFORE = 'LAST_YEAR_WEEKS_BEFORE',
    LAST_YEAR_CURRENT_WEEK = 'LAST_YEAR_CURRENT_WEEK',
    LAST_YEAR_LAST_WEEK = 'LAST_YEAR_LAST_WEEK',
}

export enum TimeConfigPeriodType {
    FIXED_DATE = 'FIXED_DATE',
    ASDA_MONTH = 'DAYS_BEFORE',
    ASDA_YEAR = 'CALENDAR_WEEKS_BEFORE',
    CURRENT_WEEK = 'ASDA_WEEKS_BEFORE',
    LAST_WEEK = 'MONTH',
    WEEK_BEFORE_LAST_WEEK = 'MONTHS_BEFORE',
    LAST_YEAR_CURRENT_WEEK = 'WALMART_WEEK',
    LAST_YEAR_LAST_WEEK = 'ASDA_WEEK',
    LAST_YEAR_WEEK_BEFORE_LAST_WEEK = 'ASDA_WEEK',
}

export enum TimePeriodConfigType {
    type = 'type',
    asdaWeek = 'asdaWeek',
    asdaMonth = 'asdaMonth',
    asdaYear = 'asdaYear',
    asdaYearFiscal = 'asdaYearFiscal',
    currentWeek = 'currentWeek',
    lastYearCurrentWeek = 'lastYearCurrentWeek',
    lastXWeeks = 'lastXWeeks',
    lastYearLastXWeeks = 'lastYearLastXWeeks',
    before = 'before',
    lastYearWeekXBefore = 'lastYearWeekXBefore',
    weekXBefore = 'weekXBefore',
    fixedDate = 'fixedDate',
    from = 'from', //from fixed date
    to = 'to', //to fixed date
    yearsBefore = 'yearsBefore',
    yearType = 'yearType',
    effectiveDate = 'effectiveDate',
    selectedYearControl = 'selectedYearControl',
}

export enum TimeConfigYearType {
    ASDA_FISCAL_YEAR_TO_DATE = 'Fiscal year to date',
    ASDA_FULL_YEAR = 'Full year',
    FISCAL = 'FISCAL',
    CALENDAR = 'CALENDAR',
    CURRENT_YEAR = 'CURRENT_YEAR',
}

export interface IReportRunDownloadLink {
    url: string;
}

export interface IRunNowOutputFile {
    id: number;
    type: FolderOutputStyle;
    createdAt: string;
    filesize: number;
    filename?: string;
    runId?: any;
}

export interface IConfigResponse {
    key: ConfigKey;
    value: string | number;
}

export interface IConfigRequest {
    value: string;
}

export enum ConfigKey {
    DSS_HELP_URL_ASDA = 'HELP_URL_ASDA',
    DSS_HELP_URL_SUPPLIER = 'HELP_URL_SUPPLIER',
    POWERBI_URL = 'POWERBI_URL',
    RUNS_REFRESH_TIMER = 'RUNS_REFRESH_TIMER',
    NOTICE = 'NOTICE',
    NOTICE_BOARD = 'NOTICE_BOARD',
    NOTICE_BOARD_FOOTER_SUPPLIER = 'NOTICE_BOARD_FOOTER_SUPPLIER',
    NOTICE_BOARD_FOOTER_ASDA = 'NOTICE_BOARD_FOOTER_ASDA',
    FORMATTER_MAX_ROWS = 'FORMATTER_MAX_ROWS',
    FORMATTER_MAX_CELLS = 'FORMATTER_MAX_CELLS',
    NOTIFY_EMAILS = 'NOTIFY_EMAILS',
    PUBLIC_VERSION = 'PUBLIC_VERSION',
    NOTICE_BOARD_SECOND_FOOTER_ASDA = 'NOTICE_BOARD_SECOND_FOOTER_ASDA',
    NOTICE_BOARD_SECOND_FOOTER_SUPPLIER = 'NOTICE_BOARD_SECOND_FOOTER_SUPPLIER',
    FORMATTER_EXCEL_TOTAL_ROWS = 'FORMATTER_EXCEL_TOTAL_ROWS',
    USER_IDLE_TIME = 'USER_IDLE_TIME',
    MAINTENANCE_MODE = 'MAINTENANCE_MODE',
    MAINTENANCE_MODE_MESSAGE = 'MAINTENANCE_MODE_MESSAGE',
    ADHOC_MAX_FILE_SIZE_MB = 'ADHOC_MAX_FILE_SIZE_MB',
    MAX_TIME_CONFIGS = 'MAX_TIME_CONFIGS',
}

export interface IFolderNode {
    id: number;
    parentId: number;
    name: string;
    createdBy: string;
    createdAt: string;
    updatedBy: string;
    updatedAt: string;
    reports: IFolderReport[] | null;
    children: IFolderNode[] | null;
    reportData?: IFolderReport;
    path?: string;
    hasRunInProgress?: boolean;
}

export interface IFolderReport {
    id: number;
    name: string;
    createdBy: string;
    createdAt: string;
    updatedBy: string;
    updatedAt: string;
    templateId: number;
    templateName: string;
    outputStyle: FolderOutputStyle;
    isShared: boolean;
    nextRunOn?: string;
    folderId: number;
    runStats?: number[];
    sharedBy?: string;
    isHidden?: boolean;
    isAdhoc: boolean;
    isPredefined?: boolean;
    isNotificationEnabled?: boolean;
}

export enum FolderOutputStyle {
    CSV = 'CSV',
    EXCEL = 'EXCEL',
    CSV_ZIP = 'CSV_ZIP',
    EXCEL_ZIP = 'EXCEL_ZIP',
}

export interface IPredefinedReport {
    id: number;
    name: string;
    createdBy: string;
    createdAt: string;
    updatedBy: string;
    updatedAt: string;
    templateId: number;
    templateName: string;
    outputStyle: FolderOutputStyle;
    isShared: boolean;
    nextRunOn?: string;
    runStats?: number[];
    lastRunScheduledAt?: string;
    sharedBy?: string;
    isVisibleExternally?: boolean;
    isPredefined?: boolean;
}

export interface IFolderCreateRequest {
    parentId: number;
    name: string;
}

export interface IFolderEditRequest {
    name: string;
}

export interface IReportRequest {
    id?: number;
    name: string;
    templateId: number;
    folderId?: number;
    outputStyle?: ReportOutputStyle;
    isScheduled?: boolean;
    scheduleStart?: string;
    scheduleEnd?: string;
    frequencyType?: ReportFrequencyType;
    frequencyOnceDate?: string;
    frequencyDayOfTheWeek?: ReportFrequencyDayOfTheWeek;
    frequencyDayOfTheMonth?: number;
    frequencyMonth?: number;
    config: IReportConfigStage[];
    timeConfig: IReportTimeConfig[];
    isHidden?: boolean;
    isNotificationEnabled?: boolean;
}

export interface IReportEditRequest {
    name: string;
    folderId?: number;
    outputStyle?: ReportOutputStyle;
    isScheduled?: boolean;
    scheduleStart?: string;
    scheduleEnd?: string;
    frequencyType?: ReportFrequencyType;
    frequencyOnceDate?: string;
    frequencyDayOfTheWeek?: ReportFrequencyDayOfTheWeek;
    frequencyDayOfTheMonth?: number;
    frequencyMonth?: number;
    config: IReportConfigStage[];
    timeConfig: IReportTimeConfig[];
    isNotificationEnabled?: boolean;
}

export interface IReportNode {
    id: number;
    filterOperation?: ReportNodeFilterOperation;
    filters?: IReportNodeFilter[];
    name?: string;
    displayName?: string;
    order?: number;
    nodePath?: IReportNodePath[];
    isFixed: boolean;
}

export interface IReportNodePath {
    id: number;
    name: string;
}

export enum ReportNodeFilterOperation {
    AND = 'AND',
    OR = 'OR',
}

export enum ReportFrequencyType {
    NONE = 'NONE',
    ONCE = 'ONCE',
    DAILY = 'DAILY',
    WEEKLY = 'WEEKLY',
    MONTHLY = 'MONTHLY',
    QUARTERLY = 'QUATERLY',
    YEARLY = 'YEARLY',
}

export enum ReportFrequencyDayOfTheWeek {
    MONDAY = 'MONDAY',
    TUESDAY = 'TUESDAY',
    WEDNESDAY = 'WEDNESDAY',
    THURSDAY = 'THURSDAY',
    FRIDAY = 'FRIDAY',
    SATURDAY = 'SATURDAY',
    SUNDAY = 'SUNDAY',
}

export enum NodeFormatType {
    DEFAULT = 'DEFAULT',
    CURRENCY = 'CURRENCY',
    PERCENTAGE = 'PERCENTAGE',
    STRING = 'STRING',
    MULTILINE_STRING = 'MULTILINE_STRING',
    DECIMAL = 'DECIMAL',
    STRING_LTRIM = 'STRING:LTRIM',
}

export enum ReportOutputStyle {
    EXCEL = 'EXCEL',
    CSV = 'CSV',
}

export interface IReportNodeFilter {
    filterType: TemplateStageSectionNodeFilter;
    values: string[];
}

export interface IReportSection {
    id: number;
    name: string;
    nodes: IReportNode[];
}

export interface IReportConfigStage {
    stage: TemplateStageType;
    sections: IReportSection[];
    stageFilterOperation?: ReportNodeFilterOperation;
}

export interface IReportEditFolderRequest {
    folderId: number;
}

export interface IReportEditNextRunOnRequest {
    nextRunOn: string;
}

export interface IReportSharedUser {
    user: string;
}

export interface IEditNodeRequest {
    datamartTable?: string;
    datamartColumn?: string;
    datamartValue?: string;
    measureId?: number;
    measureName?: string;
    measureTables?: string;
    measureValue?: string;
    measureColumns?: string;
    formatType?: NodeFormatType;
}

export interface IRunSqlResponse {
    headers: string[];
    rows: string[][];
}

export interface IUsersResponse {
    users: IUser[];
}

export interface IUser {
    id: number;
    email: string;
    isDeleted: boolean;
    vendorId: string;
    lastSeenAt?: string;
    onboarded?: boolean;
    isQueryViewer: boolean;
    notificationEnabled: boolean;
    permissions: string[];
}

export interface IUserExistsResponse {
    isUserExists: boolean;
}

export interface IStatusDashboardResponse {
    runnerQueue: IStatusReportRunResponse[];
    runnerInProgress: IStatusReportRunResponse[];
    formatterQueue: IStatusReportRunResponse[];
    formatterInProgress: IStatusReportRunResponse[];
    completed?: IStatusReportRunResponse[];
    failed?: IStatusReportRunResponse[];
}

export interface IStatusReportRunResponse {
    id: number;
    reportId: number;
    reportName: string;
    createdBy: string;
    createdAt: string;
    errorLog: string;
    hadError: boolean;
    sqlQuery: string;
    status: ReportRunStatus;
    runType: ReportRunType;
    queryCompletedAt: string;
    queryStartedAt: string;
    formatCompletedAt: string;
    formatStartedAt: string;
    isHidden: boolean;
    retryCount: number;
    lastRetryFailedAt: string;
    rowCount: number;
    columnCount: number;
    outputFile?: IRunNowOutputFile[];
}

export enum UserActivity {
    LOGIN = 'LOGIN',
    CREATE_REPORT = 'CREATE_REPORT',
    DELETE_REPORT = 'DELETE_REPORT',
    RUN_REPORT = 'RUN_REPORT',
    DOWNLOAD = 'DOWNLOAD',
    SHARE_REPORT = 'SHARE_REPORT',
    RESCHEDULE_REPORT = 'RESCHEDULE_REPORT',
    MOVE_REPORT = 'MOVE_REPORT',
    UPDATED_REPORT = 'UPDATED_REPORT',
    DELETE_RUN = 'DELETE_RUN',
    ADMIN_RUN_SQL = 'ADMIN_RUN_SQL',
    ADMIN_UPLOAD_METADATA = 'ADMIN_UPLOAD_METADATA',
    ADMIN_MODIFY_USER = 'ADMIN_MODIFY_USER',
    ADMIN_ADD_USER = 'ADMIN_ADD_USER',
    ADMIN_DELETE_USER = 'ADMIN_DELETE_USER',
    ADMIN_PREHEAT_DATAMART = 'ADMIN_PREHEAT_DATAMART',
    ADMIN_MODIFY_NODE = 'ADMIN_MODIFY_NODE',
    RUN_COMPLETED = 'RUN_COMPLETED',
    RUN_FAILED = 'RUN_FAILED',
    ADMIN_IMPORTED_DATAMART = 'ADMIN_IMPORTED_DATAMART',
}

export interface IUserActivityResponse {
    userActivities: IUserActivity[];
}

export interface IUserActivity {
    activity: UserActivity;
    email: string;
    id: number;
    reference: string;
    timestamp: string;
}

export interface IReportDetail {
    sqlQuery?: string;
    errorLog?: string;
    queryStartedAt: string;
    queryCompletedAt: string;
    formatStartedAt?: string;
    formatCompletedAt?: string;
}

export interface ITableRelsResponse {
    tableRelationships: ITableRel[];
}

export interface ITableRel {
    id: number;
    table1: string;
    column1: string;
    table2: string;
    column2: string;
    joinType: string;
    dataType: string;
}

export interface ITableSourceOptionsResponse {
    tableNames: string[];
}

export interface IFactDetailsResponse {
    factDetails: IFactDetails[];
}

export interface IFactDetails {
    id: number;
    source: string;
    table: string;
    timeColumn: string;
    timeColumnType: string;
    granularity: string;
    rlsType: string;
    canDelete: boolean;
}

export interface ISqlViewsResponse {
    sqlViews: ISqlViews[];
}

export interface ISqlViews {
    viewName: string;
    definition: string;
    canDelete: false;
}

export interface ISectionsResponse {
    sections: ISections[];
}

export interface ISections {
    id: number;
    name: string;
    type: string;
    dataType: string;
    isActive: boolean;
    canDelete: boolean;
    stageType?: string;
    allowEditing: boolean;
}

export interface ITemplatesResponse {
    templates: ITemplates[];
}

export interface IStages {
    templateId: number;
    stageType: string;
    isMandatory: boolean;
    isDeleted: boolean;
}

export interface ITemplateSection {
    templateId: number;
    stageType: string;
    sectionId: number;
    sectionName?: string;
    dataType: string;
    isDeleted: boolean;
}

export interface ITemplates {
    id: number;
    name: string;
    templateType: string;
    dataType: string;
    isActive: boolean;
    canDelete: boolean;
    description: string;
    isFuture: boolean;
    timeTable: string;
    timeColumn: string;
    fixedTemplateView: string;
    features: string;
    asdaWeekRange: string;
    visibility: string;
    dateType: string;
    pivotTable: string;
    stages: IStages[];
    sections: ITemplateSection[];
}

export interface ISectionNodesResponse {
    sectionNodes: ISectionNode[];
}

export interface ISectionNode {
    id: number;
    parentId?: number;
    name: string;
    formatHint?: string;
    isActive: boolean;
    isSelectable: boolean;
    datamartTable?: string;
    datamartColumn?: string;
    datamartValue?: string;
    dataType?: string;
    validationRegex?: string;
    availableFilters?: string;
    desc?: string;
    measureId?: number;
    formatType: string;
    visibility?: string;
    columnFunction?: string;
    canDelete?: boolean;
    children?: ISectionNode[];
    level?: number;
    expanded?: boolean;
    type: string;
    sectionId?: number;
    templateId?: number;
    measureColumns?: string;
    measureTables?: string;
    measureValue?: string;
    measureName?: string;
    templateDataType?: string;
}

export interface IAdhocReport {
    id: number;
    ownerEmail: string;
    fileSizeBytes: number;
    fileType: string;
    title: string;
    description?: string;
    uploadDate: string;
    isActive: boolean;
    shares: IAdhocShare[];
}

export interface IAdhocShare {
    id: number;
    adHocReportId: number;
    authUserEmail: string;
    sharedByEmail: string;
    sharedAt: string;
}

export interface IFileUploadRequest {
    fileName: string;
    fileSize: number;
    title: string;
    description?: string;
    sharedWithEmails?: string[];
}

export interface IFileUploadResponse {
    sasUrl: string;
    reportId: number;
    blobUrl: string;
}

export interface IFileUploadCompleteRequest {
    reportId: number;
}

export interface IChunkUploadRequest {
    fileName: string;
    fileSize: number;
    fileType: string;
    title: string;
    description?: string;
    sharedWithEmails?: string[];
}

export interface IChunkUploadResponse {
    uploadId: string;
    reportId: number;
}

export interface IChunkCompleteRequest {
    uploadId: string;
    totalChunks: number;
}

export enum AppPermissions {
    canWriteTemplate = 'manage.template.write',
    canExecuteSql = 'manage.sql.execute',
    canWriteMetadata = 'manage.metadata.write',
    canReadUsers = 'manage.users.read',
    canWriteUsers = 'manage.users.write',
    canReadDashboard = 'manage.dashboard.read',
    canWriteConfig = 'manage.config.write',

    canReadReports = 'reports.read',
    canWriteReports = 'reports.write',

    canReadRuns = 'runs.read',
    canWriteRuns = 'runs.write',

    canReadTemplates = 'templates.read',
    canReadFolders = 'folders.read',
    canWriteFolders = 'folders.write',
    canReadCalendar = 'calendar.read',

    canReadRestrictedTemplates = 'templates.readRestricted',

    canUploadAdhocReports = 'adhocReports.upload',
    canShareAdhocReports = 'adhocReports.share',
}

export enum TemplateDataType {
    OldCo = 'OldCo',
    NewCo = 'NewCo',
}

export enum TemplateVisibilityType {
    External = 'External',
    PowerUser = 'PowerUser',
    Internal = 'Internal',
}

export interface ITemplateResponse {
    templateId: number;
    success: boolean;
}

export interface ITemplateDetail {
    name: string;
    description: string;
    timeType: string;
    isActive: boolean;
    isPredefinedTemplate: boolean;
    dataType: string;
    asdaWeekRange: number | null;
    visibility: string;
    stages: INewTemplateStage[];
    sections: INewTemplateSection[];
    sectionNodes: INewTemplateSectionNode[];
}

export interface INewTemplateStage {
    isMandatory: boolean;
    stageType: TemplateStageType;
    order: number;
}

export interface INewTemplateSection {
    id: number;
    stageType: TemplateStageType;
    order: number;
}

export interface INewTemplateSectionNode {
    sectionId: number;
    id: number;
    type: string;
}

export interface IStage {
    stageType: TemplateStageType;
    description: string;
}

export enum SelectionType {
    EXCLUDE = 'EXCLUDE',
    INCLUDE = 'INCLUDE',
    FIXED = 'FIXED',
    MIXED = 'MIXED',
}

export interface TemplateMetadata {
    templateId: number | null;
    name: string;
    description: string;
    timeType: TemplateTimeType | null;
    isFutureBased: boolean;
    isActive: boolean;
    isPredefinedTemplate: boolean;
    dataType: string;
    asdaWeekRange: number | null;
    visibility: string;
    stages: ITemplateStage[];
    sections: ITemplateStageSection[];
    sectionNodes: ITemplateStageSectionNode[];
}

export interface ITemplateStageSectionNodeUI extends ITemplateStageSectionNode {
    isExpanded?: boolean;
    isSelected?: boolean;
}
